'use client';

import {
  WizardPanelBody,
  WizardPanelCard,
  WizardPanelHeader,
  WizardFormSection,
  Field,
  inputClass,
  textareaClass,
} from '@/components/event-wizard/wizard-ui';
import { RadioCard, ToggleRow } from '@/components/event-wizard/wizard-form-controls';
import type { ParticipationFieldErrors, ParticipationFormState } from '@/components/event-wizard/participation-utils';

type Props = {
  form: ParticipationFormState;
  onChange: (form: ParticipationFormState) => void;
  teamAllowed?: boolean;
  fieldErrors?: ParticipationFieldErrors;
};

export function ParticipationRulesPanel({ form, onChange, teamAllowed: _teamAllowed, fieldErrors = {} }: Props) {
  const patch = (partial: Partial<ParticipationFormState>) => onChange({ ...form, ...partial });
  const patchReg = (partial: Partial<ParticipationFormState['registration']>) =>
    onChange({ ...form, registration: { ...form.registration, ...partial } });
  const patchTeam = (partial: Partial<ParticipationFormState['team']>) =>
    onChange({ ...form, team: { ...form.team, ...partial } });

  return (
    <WizardPanelCard>
      <WizardPanelHeader
        embedded
        title="Registration settings"
        description="Control approval, waitlists, duplicates, and optional submissions."
      />

      <WizardPanelBody>
        <WizardFormSection
          title="Admission & approval rules"
          description="Control approval, waitlists, duplicates, and optional submissions."
        >
          <div className="space-y-3">
            <ToggleRow
              label="Approval required"
              hint="Participants require organizer approval before joining."
              checked={form.registration.approval_required}
              onChange={(checked) => patchReg({ approval_required: checked })}
            />
            <ToggleRow
              label="Enable waitlist"
              hint="When tickets are full, participants join a waitlist."
              checked={form.registration.waitlist_enabled}
              onChange={(checked) => patchReg({ waitlist_enabled: checked })}
            />
            <ToggleRow
              label="Allow duplicate registrations"
              hint="Allow the same participant to register multiple times."
              checked={form.registration.allow_duplicate_registrations}
              onChange={(checked) => patchReg({ allow_duplicate_registrations: checked })}
            />
            <ToggleRow
              label="Submission required"
              hint="Require participants to submit work before or during the event."
              checked={form.registration.submission_required}
              onChange={(checked) => patchReg({ submission_required: checked })}
            />
          </div>

          {form.registration.submission_required && (
            <div className="space-y-5 border-t border-nature-border/70 pt-6">
              <Field label="Submission deadline" required error={fieldErrors.submission_deadline}>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={form.registration.submission_deadline}
                  onChange={(e) => patchReg({ submission_deadline: e.target.value })}
                />
              </Field>
              <ToggleRow
                label="File upload allowed"
                hint="Let participants attach files with their submission."
                checked={form.registration.file_upload_allowed}
                onChange={(checked) => patchReg({ file_upload_allowed: checked })}
              />
              <Field label="Submission instructions" hint="Tell participants what to submit and how.">
                <textarea
                  className={textareaClass}
                  value={form.registration.submission_instructions}
                  onChange={(e) => patchReg({ submission_instructions: e.target.value })}
                  placeholder="Describe submission format, links, or file requirements."
                />
              </Field>
            </div>
          )}
        </WizardFormSection>

        <WizardFormSection
          title="Team settings"
          description="Choose individual or team registration and set team size limits."
          divider
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <RadioCard
              label="Individual"
              selected={form.registration_type === 'individual'}
              onSelect={() => patch({ registration_type: 'individual' })}
            />
            <RadioCard
              label="Team"
              selected={form.registration_type === 'team'}
              onSelect={() => patch({ registration_type: 'team' })}
            />
          </div>
          {form.registration_type === 'team' ? (
            <div className="grid gap-6 border-t border-nature-border/70 pt-6 sm:grid-cols-2">
              <Field label="Min team size" error={fieldErrors.team_min} hint="Controls team creation limits.">
                <input
                  type="number"
                  min={1}
                  className={inputClass}
                  value={form.team.min_size}
                  onChange={(e) => patchTeam({ min_size: Number(e.target.value) || 1 })}
                />
              </Field>
              <Field label="Max team size" error={fieldErrors.team_max}>
                <input
                  type="number"
                  min={1}
                  className={inputClass}
                  value={form.team.max_size}
                  onChange={(e) => patchTeam({ max_size: Number(e.target.value) || 1 })}
                />
              </Field>
            </div>
          ) : null}
        </WizardFormSection>
      </WizardPanelBody>
    </WizardPanelCard>
  );
}
